import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import { toast } from 'react-hot-toast';
import DeleteConfirmationModal from '../../components/common/DeleteConfirmationModal';
import './Settings.css';

function StoreLists({ onBack }) {
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        itemsPerPage: 20
    });

    // Edit state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({});

    // New store modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [newStore, setNewStore] = useState({
        location_pc: '',
        corp: '',
        state: '',
        hr: '',
        vp: '',
        director: '',
        sr_mum: '',
        mum: '',
        flm: '',
        is_active: true
    });

    // Delete Modal State
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        storeId: null,
        storeName: null
    });

    useEffect(() => {
        fetchStores(pagination.currentPage);
    }, [pagination.currentPage]);

    const fetchStores = async (page) => {
        try {
            setLoading(true);
            const response = await apiClient.get(`/store_list?page=${page}&limit=20`);
            setStores(response.data.data);
            setPagination(response.data.pagination);
        } catch (error) {
            console.error('Error fetching stores:', error);
            toast.error('Failed to load stores');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, currentPage: newPage }));
        }
    };

    // Delete Store
    const handleDelete = (store) => {
        setDeleteModal({
            isOpen: true,
            storeId: store.location_pc,
            storeName: store.corp
        });
    };

    const handleConfirmDelete = async () => {
        try {
            await apiClient.delete(`/store_list/${deleteModal.storeId}`);
            toast.success('Store deleted successfully');
            fetchStores(pagination.currentPage);
        } catch (error) {
            console.error('Error deleting store:', error);
            toast.error('Failed to delete store');
        } finally {
            setDeleteModal({ isOpen: false, storeId: null, storeName: null });
        }
    };

    // Start Editing
    const handleEditClick = (store) => {
        setEditForm({ ...store });
        setShowEditModal(true);
    };

    // Handle Edit Change
    const handleEditChange = (e, field) => {
        setEditForm(prev => ({ ...prev, [field]: e.target.value }));
    };

    // Save Edit
    const handleSaveEdit = async (e) => {
        e.preventDefault();
        try {
            await apiClient.put(`/store_list/${editForm.location_pc}`, editForm);
            toast.success('Store updated successfully');
            setShowEditModal(false);
            setEditForm({});
            fetchStores(pagination.currentPage);
        } catch (error) {
            console.error('Error updating store:', error);
            toast.error('Failed to update store');
        }
    };

    // Handle Add Change
    const handleAddChange = (e, field) => {
        setNewStore(prev => ({ ...prev, [field]: e.target.value }));
    };

    // Create Store
    const handleCreateStore = async (e) => {
        e.preventDefault();
        try {
            await apiClient.post('/store_list', newStore);
            toast.success('Store created successfully');
            setShowAddModal(false);
            setNewStore({
                location_pc: '', corp: '', state: '', hr: '',
                vp: '', director: '',
                sr_mum: '', mum: '',
                flm: '', is_active: true
            });
            fetchStores(1);
        } catch (error) {
            console.error('Error creating store:', error);
            toast.error('Failed to create store');
        }
    };

    return (
        <div className="settings-component-wrapper">
            <div className="settings-component-content">
                <div className="settings-component-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h1>🏪 Store Lists</h1>
                        <p>Manage store locations and hierarchy</p>
                    </div>
                    <button className="settings-primary-button" onClick={onBack}>
                        ← Back to HR Settings
                    </button>
                </div>

                <div className="settings-component-body">
                    {loading ? (
                        <div className="settings-loading-state">Loading...</div>
                    ) : (
                        <>
                            <div className="settings-table-container">
                                <table className="settings-table">
                                    <thead>
                                        <tr>
                                            <th>Store PC</th>
                                            <th>Store Name</th>
                                            <th>Market</th>
                                            <th>HR Rep</th>
                                            <th>Director</th>
                                            <th>MUM</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stores.map(store => (
                                            <tr key={store.location_pc}>
                                                <td>{store.location_pc}</td>
                                                <td>{store.corp}</td>
                                                <td>{store.state}</td>
                                                <td>{store.hr}</td>
                                                <td>{store.director}</td>
                                                <td>{store.mum}</td>
                                                <td>
                                                    <div className="settings-action-buttons">
                                                        <button className="settings-edit-button" onClick={() => handleEditClick(store)} title="Edit">✏️</button>
                                                        <button className="settings-delete-button" onClick={() => handleDelete(store)} title="Delete">🗑️</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="settings-pagination">
                                <button
                                    disabled={pagination.currentPage === 1}
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                >
                                    Previous
                                </button>
                                <span>Page {pagination.currentPage} of {pagination.totalPages}</span>
                                <button
                                    disabled={pagination.currentPage === pagination.totalPages}
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Add Store Modal */}
            {showAddModal && (
                <div className="settings-modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="settings-modal-content" style={{ maxWidth: '800px' }} onClick={(e) => e.stopPropagation()}>
                        <div className="settings-modal-header">
                            <h2>Add New Store</h2>
                            <button className="settings-close-button" onClick={() => setShowAddModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleCreateStore} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                            <div className="settings-form-group">
                                <label>Store PC</label>
                                <input type="number" required value={newStore.location_pc} onChange={(e) => handleAddChange(e, 'location_pc')} />
                            </div>
                            <div className="settings-form-group">
                                <label>Store Name (Corp)</label>
                                <input type="text" required value={newStore.corp} onChange={(e) => handleAddChange(e, 'corp')} />
                            </div>
                            <div className="settings-form-group">
                                <label>Market (State)</label>
                                <input type="text" value={newStore.state} onChange={(e) => handleAddChange(e, 'state')} />
                            </div>
                            <div className="settings-form-group">
                                <label>HR Rep</label>
                                <input type="text" value={newStore.hr} onChange={(e) => handleAddChange(e, 'hr')} />
                            </div>
                            <div className="settings-form-group">
                                <label>Director</label>
                                <input type="text" value={newStore.director} onChange={(e) => handleAddChange(e, 'director')} />
                            </div>
                            <div className="settings-form-group">
                                <label>MUM</label>
                                <input type="text" value={newStore.mum} onChange={(e) => handleAddChange(e, 'mum')} />
                            </div>
                            <div className="settings-form-group">
                                <label>VP</label>
                                <input type="text" value={newStore.vp} onChange={(e) => handleAddChange(e, 'vp')} />
                            </div>
                            <div className="settings-form-group">
                                <label>FLM</label>
                                <input type="text" value={newStore.flm} onChange={(e) => handleAddChange(e, 'flm')} />
                            </div>

                            <div className="settings-modal-actions" style={{ gridColumn: '1 / -1' }}>
                                <button type="button" className="settings-cancel-button" onClick={() => setShowAddModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="settings-submit-button">
                                    Create Store
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Store Modal */}
            {showEditModal && (
                <div className="settings-modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="settings-modal-content" style={{ maxWidth: '800px' }} onClick={(e) => e.stopPropagation()}>
                        <div className="settings-modal-header">
                            <h2>Edit Store</h2>
                            <button className="settings-close-button" onClick={() => setShowEditModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleSaveEdit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                            <div className="settings-form-group">
                                <label>Store PC</label>
                                <input type="number" disabled value={editForm.location_pc || ''} style={{ backgroundColor: '#f0f0f0' }} />
                            </div>
                            <div className="settings-form-group">
                                <label>Store Name (Corp)</label>
                                <input type="text" required value={editForm.corp || ''} onChange={(e) => handleEditChange(e, 'corp')} />
                            </div>
                            <div className="settings-form-group">
                                <label>Market (State)</label>
                                <input type="text" value={editForm.state || ''} onChange={(e) => handleEditChange(e, 'state')} />
                            </div>
                            <div className="settings-form-group">
                                <label>HR Rep</label>
                                <input type="text" value={editForm.hr || ''} onChange={(e) => handleEditChange(e, 'hr')} />
                            </div>
                            <div className="settings-form-group">
                                <label>Director</label>
                                <input type="text" value={editForm.director || ''} onChange={(e) => handleEditChange(e, 'director')} />
                            </div>
                            <div className="settings-form-group">
                                <label>MUM</label>
                                <input type="text" value={editForm.mum || ''} onChange={(e) => handleEditChange(e, 'mum')} />
                            </div>
                            <div className="settings-form-group">
                                <label>VP</label>
                                <input type="text" value={editForm.vp || ''} onChange={(e) => handleEditChange(e, 'vp')} />
                            </div>
                            <div className="settings-form-group">
                                <label>FLM</label>
                                <input type="text" value={editForm.flm || ''} onChange={(e) => handleEditChange(e, 'flm')} />
                            </div>

                            <div className="settings-modal-actions" style={{ gridColumn: '1 / -1' }}>
                                <button type="button" className="settings-cancel-button" onClick={() => setShowEditModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="settings-submit-button">
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <DeleteConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, storeId: null, storeName: null })}
                onConfirm={handleConfirmDelete}
                itemName={deleteModal.storeName}
            />
        </div>
    );
}

export default StoreLists;
